// Side-by-side scenario success rates.
//
// Renders one row per scenario (Base case / Inflation Spike / Conflict
// / etc.) so the user can see how their probability of success bends
// under different macro regimes at a glance. The success bar is a
// hand-rolled <progress>-like div so the row stays dependency-free and
// scales without Recharts re-laying out on every refresh.

import type { ScenarioComparisonRow } from '../api/goalAlignmentApi';

interface ScenarioComparisonTableProps {
    rows: ScenarioComparisonRow[];
    /** When set, the matching row is highlighted with a ring so the user
     *  can see which scenario is currently driving the histogram. */
    highlightScenario?: string | null;
    /** Which dual-path reality to display. Defaults to the After-Tax
     *  (net) numbers so the table matches the page's default view. */
    viewMode?: 'before' | 'after';
}

function pctLabel(p: number): string {
    return `${(p * 100).toFixed(0)}%`;
}

function levelClass(level?: string): string {
    if (level === 'strong') return 'text-emerald-600 dark:text-emerald-400';
    if (level === 'on-track') return 'text-amber-600 dark:text-amber-400';
    if (level === 'at-risk') return 'text-red-600 dark:text-red-400';
    return 'text-slate-700 dark:text-slate-300';
}

function barColour(level?: string): string {
    if (level === 'strong') return 'bg-emerald-500';
    if (level === 'on-track') return 'bg-amber-500';
    if (level === 'at-risk') return 'bg-red-500';
    return 'bg-slate-500';
}

export const ScenarioComparisonTable = ({
    rows,
    highlightScenario,
    viewMode = 'after',
}: ScenarioComparisonTableProps) => (
    <div className="overflow-x-auto">
        <table className="w-full text-sm">
            <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800 text-left">
                    <th className="py-2 pr-4 text-xs font-bold uppercase tracking-wide muted">
                        Scenario
                    </th>
                    <th className="py-2 px-2 text-right text-xs font-bold uppercase tracking-wide muted">
                        Success rate
                    </th>
                    <th className="py-2 px-2 text-xs font-bold uppercase tracking-wide muted">
                        Distribution
                    </th>
                    <th className="py-2 pl-2 text-right text-xs font-bold uppercase tracking-wide muted">
                        Median outcome
                    </th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row) => {
                    const isHighlight =
                        highlightScenario != null &&
                        (row.scenario === highlightScenario ||
                            (highlightScenario === '' &&
                                row.scenario === 'Base case'));

                    if (row.error) {
                        return (
                            <tr
                                key={row.scenario}
                                className="border-b border-slate-100 dark:border-slate-800/60 last:border-b-0"
                            >
                                <td className="py-2 pr-4 font-semibold text-slate-900 dark:text-slate-100">
                                    {row.scenario}
                                </td>
                                <td
                                    colSpan={3}
                                    className="py-2 text-xs italic text-red-600 dark:text-red-400"
                                >
                                    {row.error}
                                </td>
                            </tr>
                        );
                    }

                    // Read the metrics for the requested dual-path reality,
                    // falling back to the legacy flat row fields if the
                    // backend response predates the before/after split.
                    const metrics =
                        viewMode === 'before' ? row.before_tax : row.after_tax;
                    const prob =
                        metrics?.probability_of_success ??
                        row.probability_of_success ??
                        0;
                    const median = metrics?.median ?? row.median;
                    return (
                        <tr
                            key={row.scenario}
                            className={[
                                'border-b border-slate-100 dark:border-slate-800/60 last:border-b-0',
                                isHighlight
                                    ? 'bg-emerald-50/60 dark:bg-emerald-950/30'
                                    : '',
                            ].join(' ')}
                        >
                            <td className="py-2 pr-4 font-semibold text-slate-900 dark:text-slate-100">
                                {row.scenario}
                            </td>
                            <td
                                className={[
                                    'py-2 px-2 text-right tabular-nums font-bold',
                                    levelClass(row.feedback_level),
                                ].join(' ')}
                            >
                                {pctLabel(prob)}
                            </td>
                            <td className="py-2 px-2 min-w-[10rem]">
                                <div className="h-2 w-full rounded-full bg-slate-200 dark:bg-slate-800">
                                    <div
                                        className={[
                                            'h-2 rounded-full transition-all duration-500',
                                            barColour(row.feedback_level),
                                        ].join(' ')}
                                        style={{
                                            width: `${Math.min(100, prob * 100)}%`,
                                        }}
                                    />
                                </div>
                            </td>
                            <td className="py-2 pl-2 text-right tabular-nums">
                                {median != null
                                    ? median.toLocaleString(undefined, {
                                          style: 'currency',
                                          currency: 'USD',
                                          maximumFractionDigits: 0,
                                      })
                                    : '—'}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    </div>
);

export default ScenarioComparisonTable;
